import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '@/shared/store/hooks';
import {
  useAddBankMutation,
  useBankDetailsQuery,
  useDeleteBankMutation,
  useWithdrawDeductionMutation,
  useWithdrawalMutation,
} from '../../api/useWalletQueries';
import type { Bank, BankForm, DeductionBlock, WithdrawModalProps } from './WithdrawModal.types';

/** Owns the bank-select → amount two-step withdraw flow: bank CRUD, deduction preview, and submission. */
export function useWithdrawModalState({ open, onClose, currentBalance }: WithdrawModalProps) {
  const user = useAppSelector((s) => s.auth.user);
  const [step, setStep] = useState<'bank' | 'amount'>('bank');
  const [adding, setAdding] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [deductions, setDeductions] = useState<{
    qndt?: DeductionBlock;
    normal?: DeductionBlock;
  } | null>(null);
  const [totals, setTotals] = useState<{
    totalDeduction: number;
    finalPayout: number;
  } | null>(null);

  const banksQ = useBankDetailsQuery(open);
  const addBank = useAddBankMutation();
  const deleteBank = useDeleteBankMutation();
  const withdrawMut = useWithdrawalMutation();
  const taxMut = useWithdrawDeductionMutation();

  const banks: Bank[] = banksQ.data?.data || [];
  const banksLoading = banksQ.isPending;
  const bankIdsKey = banks.map((b) => b._id).join(',');

  const defaultContact = () => {
    const raw = String(
      user?.mobileNumber || user?.phone || user?.contact || '',
    );
    const digits = raw.replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BankForm>({
    mode: 'onChange',
    defaultValues: {
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',
      accountType: 'savings',
      email: '',
      contact: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    setStep('bank');
    // Wait for banks to load before deciding form vs list.
    setAdding(false);
    setSelectedBankId('');
    setAmount('');
    setError('');
    setDeductions(null);
    setTotals(null);
    reset({
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',
      accountType: 'savings',
      email: String(user?.email || '').trim(),
      contact: defaultContact(),
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // After banks resolve: show add form only when there are no saved banks.
  useEffect(() => {
    if (!open || banksLoading) return;

    if (banks.length === 0) {
      setAdding(true);
      setSelectedBankId('');
      return;
    }

    // Keep user's "Add new" form open if they already chose it.
    setSelectedBankId((prev) => {
      if (prev && banks.some((b) => b._id === prev)) return prev;
      return banks[0]?._id || '';
    });
  }, [open, banksLoading, banks.length, bankIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const n = Number(amount);
    if (!open || step !== 'amount' || !n || n <= 0) {
      setDeductions(null);
      setTotals(null);
      return;
    }
    const t = window.setTimeout(() => {
      taxMut.mutate(
        {
          amount: n,
          userId: (user?._id as string) || (user?.id as string),
        },
        {
          onSuccess: (res) => {
            setDeductions(res?.data?.deductions || null);
            setTotals(res?.data?.totals || null);
          },
        },
      );
    }, 400);
    return () => window.clearTimeout(t);
  }, [amount, step, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    onClose();
    setAmount('');
    setError('');
    setSelectedBankId('');
    setAdding(false);
    setDeductions(null);
  };

  const onAddBank = (data: BankForm) => {
    const digits = String(data.contact || '').replace(/\D/g, '');
    const contact = digits.length > 10 ? digits.slice(-10) : digits;

    addBank.mutate(
      {
        ...data,
        email: String(data.email || '').trim(),
        contact,
      },
      {
        onSuccess: (res) => {
          const id =
            res?.data?.bankDetails?._id ||
            res?.data?._id ||
            res?.data?.bankDetailId;
          if (id) setSelectedBankId(id);
          setAdding(false);
          reset({
            accountHolderName: '',
            accountNumber: '',
            ifscCode: '',
            bankName: '',
            branchName: '',
            accountType: 'savings',
            email: String(user?.email || '').trim(),
            contact: defaultContact(),
          });
          setStep('amount');
        },
      },
    );
  };

  const selectBank = (bankId: string) => {
    setSelectedBankId(bankId);
    setError('');
  };

  const proceedWithBank = () => {
    if (!selectedBankId) {
      setError('Select a bank account');
      return;
    }
    setError('');
    setStep('amount');
  };

  const submitWithdraw = () => {
    const n = Number(amount);
    if (!n || n < 1000) {
      setError('Minimum amount is 1,000');
      return;
    }
    if (n > 100000) {
      setError('Maximum amount is 100,000');
      return;
    }
    if (n > Number(currentBalance || 0)) {
      setError('Insufficient balance');
      return;
    }
    withdrawMut.mutate(
      { bankDetailId: selectedBankId, amount: n },
      { onSuccess: () => close() },
    );
  };

  const goBack = () => {
    if (step === 'amount') setStep('bank');
    else setAdding(false);
  };

  const breakdown = [
    ...(deductions?.qndt?.breakdown || []),
    ...(deductions?.normal?.breakdown || []),
  ].filter((i) => i?.deduction && Number(i.deduction) !== 0);

  // const net = deductions?.normal?.netAmount ?? deductions?.qndt?.netAmount ?? null;
  const net = totals?.finalPayout ?? null;
  const totalDeduction = totals?.totalDeduction ?? null;

  return {
    step,
    adding,
    setAdding,
    selectedBankId,
    amount,
    setAmount,
    error,
    setError,
    banks,
    banksLoading,
    isAddingBank: addBank.isPending,
    deleteBank: (bankId: string) => deleteBank.mutate({ bankDetailId: bankId }),
    isWithdrawing: withdrawMut.isPending,
    register,
    handleSubmit,
    formErrors: errors,
    close,
    onAddBank,
    selectBank,
    proceedWithBank,
    submitWithdraw,
    goBack,
    breakdown,
    net,
    totalDeduction,
    isCalculatingCharges: taxMut.isPending,
  };
}
